import {
	parseWorkout,
	type Section,
	type WorkoutParseError,
	type WorkoutParseResult,
} from '@dthstrnvmbr/momentum-parser'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PulseDot } from '#app/components/pulse-dot.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
/**
 * Result of parsing one section. Either success with the parsed Section,
 * or failure with the original input and an error message.
 */
export type SectionParseResult =
	| { kind: 'ok'; input: string; section: Section }
	| { kind: 'error'; input: string; error: string }

/**
 * A named set of demo examples: raw notation plus its server-parsed result.
 * The `parsed` side is deliberately loose (`SectionParseResult[]` after JSON
 * serialization through a loader) so any route can supply its own examples.
 */
export type DemoExamples = Record<
	string,
	{ notation: string; parsed: SectionParseResult[] }
>

/**
 * Parse a multi-section workout string via the parser's own `parseWorkout`
 * (non-strict mode), rather than splitting on blank lines and calling
 * `parseSection` per chunk. This picks up division markers (`~ Name ~`) for
 * free, since `parseSection` has no notion of divisions.
 *
 * Behavior: The parser does NOT resync after a syntax error — once one chunk
 * fails, everything after it (regardless of further blank-line breaks) is
 * swallowed into that single error's `chunk`/`message`. In practice this
 * means `errors` never has more than one entry, and it is always last.
 * If the FIRST section in a multi-section input is broken, ALL sections
 * are lost — the whole input becomes one `error` result — whereas the old
 * per-chunk splitter would still have parsed the later, valid chunks
 * independently. This is a real behavior change for the demo.
 *
 * Error interleaving: `errors[].index` is the 0-based position of that error
 * among the sections + errors sequence, so leading `ok` sections interleave
 * with it correctly by counting up from 0.
 */
export function parseWorkoutNotation(input: string): SectionParseResult[] {
	if (!input.trim()) return []
	try {
		const { sections, errors } = parseWorkout(input, {
			strict: false,
		}) as unknown as WorkoutParseResult
		const ok = sections.map((section) => ({
			kind: 'ok' as const,
			input: section.stringInput,
			section,
		}))
		const bad = errors.map((e: WorkoutParseError) => ({
			kind: 'error' as const,
			input: e.chunk,
			error: e.message,
			index: e.index,
		}))
		// Interleave back into input order using the error `index` (position
		// among sections + errors). Sections don't carry an index, so we walk
		// the combined sequence and pull from `ok` whenever the current slot
		// isn't a known error index.
		const out: SectionParseResult[] = []
		let s = 0
		const total = ok.length + bad.length
		const badByIndex = new Map(bad.map((b) => [b.index, b]))
		const consumed = new Set<(typeof bad)[number]>()
		for (let i = 0; i < total; i++) {
			const b = badByIndex.get(i)
			if (b) {
				out.push({ kind: 'error', input: b.input, error: b.error })
				consumed.add(b)
			} else if (s < ok.length) out.push(ok[s++]!)
		}
		// Safety net: a mismatched, out-of-range, or duplicate `errors[].index`
		// must not silently drop an error card. `badByIndex` is keyed by index,
		// so a collision (two errors sharing an index) or an index >= total
		// (never visited by the loop above) would otherwise lose that entry
		// entirely. Append anything the index-based interleave never placed.
		for (const b of bad) {
			if (!consumed.has(b)) {
				out.push({ kind: 'error', input: b.input, error: b.error })
			}
		}
		return out
	} catch (err) {
		return [
			{
				kind: 'error',
				input,
				error: err instanceof Error ? err.message : String(err),
			},
		]
	}
}

export type WorkoutNotationDemoProps = {
	initial: string
	caption?: string
	heading?: string
	description?: string
	rows?: number
	examples?: DemoExamples
	/** Hide the example-picker dropdown (e.g. in guided tutorial steps). */
	hideExampleMenu?: boolean
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function ParsedOutput({ results }: { results: SectionParseResult[] }) {
	if (results.length === 0) {
		return (
			<div className="text-mono-label text-ink-fg-soft">
				Type some workout notation to see it parsed.
			</div>
		)
	}
	return (
		<div className="flex flex-col gap-3">
			{results.map((r, i) => (
				<SectionCard key={i} result={r} />
			))}
		</div>
	)
}

function SectionCard({ result }: { result: SectionParseResult }) {
	if (result.kind === 'error') {
		return (
			<div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3">
				<p className="font-mono-display text-mono-label text-destructive">
					Parse error
				</p>
				<p className="mt-1 text-sm text-ink-fg-soft">{result.error}</p>
				<pre className="mt-2 whitespace-pre-wrap font-mono-display text-mono-label text-ink-fg-soft">
					{result.input}
				</pre>
			</div>
		)
	}
	const { section } = result
	const isCircuit = section.roundCount > 1
	const sectionRestLabel = isCircuit
		? formatDuration(
				(section as unknown as { interval?: { duration?: unknown[] } }).interval
					?.duration?.[0],
			)
		: ''
	return (
		<div className="rounded-md border border-m-rule bg-ink px-4 py-3">
			{isCircuit ? (
				<div className="mb-3 flex flex-wrap items-center gap-2">
					<span className="border-ember/40 bg-ember/10 inline-flex items-baseline gap-1.5 rounded-sm border px-2 py-0.5">
						<span className="text-eyebrow text-ember">Rounds</span>
						<span className="font-display text-sm font-medium tabular-nums text-ember">
							{section.roundCount}
						</span>
					</span>
					{sectionRestLabel ? (
						<span className="inline-flex items-baseline gap-1.5 rounded-sm border border-m-rule px-2 py-0.5">
							<span className="text-eyebrow text-ink-fg-soft">Every</span>
							<span className="font-display text-sm font-medium tabular-nums text-ink-fg">
								{sectionRestLabel}
							</span>
						</span>
					) : null}
				</div>
			) : null}
			<div className="flex flex-col gap-4">
				{section.worksets.map((ws, idx) => (
					<WorksetBlock
						key={idx}
						workset={ws}
						roundCount={section.roundCount}
						isFirst={idx === 0}
					/>
				))}
			</div>
		</div>
	)
}

/**
 * One workset = one exercise line in the source. Renders the exercise name,
 * a grid of labeled stat tiles, and (only when values vary) a compact
 * mini-table of per-set or per-round detail.
 */
function WorksetBlock({
	workset,
	roundCount,
	isFirst,
}: {
	workset: Section['worksets'][number]
	roundCount: number
	isFirst: boolean
}) {
	const isCircuit = roundCount > 1
	const totalRows = isCircuit ? roundCount : extractSetCount(workset)
	const rows = totalRows > 0 ? expandPerSet(workset, totalRows) : []
	const variance = computeVariance(rows)
	const showTable = rows.length > 1 && hasAnyVariance(variance)

	const setsLabel = isCircuit ? '' : formatSetCount(workset)
	const repsLabel = formatRepsSummary(workset)
	const intensityLabel = formatIntensitySummary(workset)
	const restLabel = formatRestSummary(workset)
	const volumeKind = volumeIsDistance(workset) ? 'Distance' : 'Reps'
	const intervalKind = intervalKindOf(workset) // 'Every' | 'Rest'

	return (
		<div className={isFirst ? '' : 'border-t border-m-rule pt-4'}>
			<h4 className="font-display text-base font-medium text-ink-fg">
				{workset.exercise?.name ?? 'Exercise'}
			</h4>
			<StatTileGrid
				tiles={[
					...(isCircuit ? [] : [{ label: 'Sets', value: setsLabel }]),
					{ label: volumeKind, value: repsLabel, varied: variance.reps },
					{
						label: 'Intensity',
						value: intensityLabel,
						varied: variance.intensity,
					},
					{ label: intervalKind, value: restLabel, varied: variance.rest },
				]}
			/>
			{showTable ? (
				<PerSetTable
					rows={rows}
					isCircuit={isCircuit}
					volumeKind={volumeKind}
					intervalKind={intervalKind}
				/>
			) : null}
		</div>
	)
}

/** Detect whether the workset's volume is distance-based (e.g., `5x400m`). */
function volumeIsDistance(workset: Section['worksets'][number]): boolean {
	const reps = (workset.volume?.[0] as { reps?: unknown })?.reps
	if (!Array.isArray(reps)) return false
	const first = reps[0]
	if (!first || typeof first !== 'object') return false
	const distance = (first as { distance?: unknown }).distance
	return typeof distance === 'string' && distance.length > 0
}

/** The parser stores `every N:NN` intervals as `type: 'inclusive'`. */
function intervalKindOf(
	workset: Section['worksets'][number],
): 'Every' | 'Rest' {
	const interval = workset.interval?.[0] as { type?: string } | undefined
	return interval?.type === 'inclusive' ? 'Every' : 'Rest'
}

// ---------------------------------------------------------------------------
// Stat tiles — small, labeled, sans-serif. Replaces the pipe-delimited summary.
// ---------------------------------------------------------------------------

type StatTile = { label: string; value: string; varied?: boolean }

function StatTileGrid({ tiles }: { tiles: StatTile[] }) {
	// Filter out tiles whose only state is "no data" (empty value AND not varied)
	// to avoid cluttering when a section omits, e.g., intensity or rest entirely.
	const visible = tiles.filter((t) => t.value || t.varied)
	if (visible.length === 0) return null
	return (
		<dl
			className="mt-3 grid gap-2"
			style={{
				gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`,
			}}
		>
			{visible.map((tile) => (
				<div
					key={tile.label}
					className="border-m-rule/60 bg-ink-soft/40 rounded-sm border px-3 py-2"
				>
					<dt className="text-eyebrow text-ink-fg-soft">{tile.label}</dt>
					<dd className="mt-1 flex items-baseline gap-1.5">
						<span className="font-display text-base font-medium tabular-nums text-ink-fg">
							{tile.value || <span className="text-ink-fg-soft/60">—</span>}
						</span>
						{tile.varied ? (
							<span
								className="text-eyebrow text-ember"
								title="Varies across sets"
							>
								varies
							</span>
						) : null}
					</dd>
				</div>
			))}
		</dl>
	)
}

// ---------------------------------------------------------------------------
// Per-set mini-table — only rendered when at least one column varies.
// ---------------------------------------------------------------------------

type Variance = {
	reps: boolean
	intensity: boolean
	rest: boolean
}

function PerSetTable({
	rows,
	isCircuit,
	volumeKind,
	intervalKind,
}: {
	rows: PerSet[]
	isCircuit: boolean
	volumeKind: 'Reps' | 'Distance'
	intervalKind: 'Every' | 'Rest'
}) {
	return (
		<div className="border-m-rule/60 mt-3 overflow-hidden rounded-sm border">
			<table className="w-full border-collapse">
				<thead>
					<tr className="bg-ink-soft/60">
						<th className="px-3 py-1.5 text-left text-eyebrow text-ink-fg-soft">
							{isCircuit ? 'Round' : 'Set'}
						</th>
						<th className="px-3 py-1.5 text-left text-eyebrow text-ink-fg-soft">
							{volumeKind}
						</th>
						<th className="px-3 py-1.5 text-left text-eyebrow text-ink-fg-soft">
							{'Intensity'}
						</th>
						<th className="px-3 py-1.5 text-left text-eyebrow text-ink-fg-soft">
							{intervalKind}
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr
							key={i}
							className={i % 2 === 1 ? 'bg-ember/[0.035]' : undefined}
						>
							<td className="px-3 py-1.5 font-mono-display text-mono-label tabular-nums text-ink-fg-soft">
								{i + 1}
							</td>
							<td className="px-3 py-1.5 font-display text-sm tabular-nums text-ink-fg">
								{formatRepValue(row.reps) || (
									<span className="text-ink-fg-soft/60">—</span>
								)}
							</td>
							<td className="px-3 py-1.5 font-display text-sm tabular-nums text-ink-fg">
								{formatRowIntensity(row) || (
									<span className="text-ink-fg-soft/60">—</span>
								)}
							</td>
							<td className="px-3 py-1.5 font-display text-sm tabular-nums text-ink-fg">
								{formatDuration(row.duration) || (
									<span className="text-ink-fg-soft/60">—</span>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

function formatRowIntensity(row: PerSet): string {
	if (row.magnitude === undefined) return ''
	if (!isIntensitySpecified(row.magnitude, row.unit)) return ''
	const m =
		typeof row.magnitude === 'number'
			? String(row.magnitude)
			: row.magnitude && typeof row.magnitude === 'object'
				? '++progression'
				: String(row.magnitude)
	return `${m}${row.unit ?? ''}`
}

// ---------------------------------------------------------------------------
// Per-set expansion (data shape used by both the variance check and the table)
// ---------------------------------------------------------------------------

/** Extract the total set count from a workset's volume. */
function extractSetCount(workset: Section['worksets'][number]): number {
	const v = workset.volume?.[0] as { sets?: unknown } | undefined
	const sets = v?.sets
	if (Array.isArray(sets) && sets.length > 0) {
		const first = sets[0]
		if (typeof first === 'number') return first
	}
	// Fall back to reps.length when reps is a per-set array
	const reps = (workset.volume?.[0] as { reps?: unknown })?.reps
	if (Array.isArray(reps)) return reps.length
	return 1
}

type PerSet = {
	reps: unknown
	magnitude: unknown
	unit: string | undefined
	duration: unknown
}

/** Expand a workset into N per-set rows, broadcasting single-entry arrays. */
function expandPerSet(
	workset: Section['worksets'][number],
	count: number,
): PerSet[] {
	const v = workset.volume?.[0] as { reps?: unknown } | undefined
	const i = workset.intensity?.[0] as
		| { magnitude?: unknown; unit?: unknown }
		| undefined
	const interval = workset.interval?.[0] as { duration?: unknown } | undefined
	const reps = v?.reps
	const magnitudes = Array.isArray(i?.magnitude) ? i.magnitude : []
	const units = Array.isArray(i?.unit) ? i.unit : []
	const durations = Array.isArray(interval?.duration) ? interval.duration : []
	return Array.from({ length: count }, (_, idx) => ({
		reps: pickAt(reps, idx),
		magnitude: pickAt(magnitudes, idx),
		unit: pickStringAt(units, idx),
		duration: pickAt(durations, idx),
	}))
}

function pickAt<T>(arr: unknown, idx: number): T | undefined {
	if (!Array.isArray(arr) || arr.length === 0) return undefined
	if (arr.length === 1) return arr[0] as T
	return (arr[idx] ?? arr[arr.length - 1]) as T
}

function pickStringAt(arr: unknown, idx: number): string | undefined {
	const v = pickAt<unknown>(arr, idx)
	return typeof v === 'string' ? v : undefined
}

// ---------------------------------------------------------------------------
// Variance detection — drives "should we even show the per-set table?"
// ---------------------------------------------------------------------------

function computeVariance(rows: PerSet[]): Variance {
	if (rows.length < 2) return { reps: false, intensity: false, rest: false }
	const repKeys = rows.map((r) => formatRepValue(r.reps))
	const intensityKeys = rows.map(
		(r) => `${stringifyMagnitude(r.magnitude)}|${r.unit ?? ''}`,
	)
	const restKeys = rows.map((r) => formatDuration(r.duration))
	return {
		reps: uniqueCount(repKeys) > 1,
		intensity: uniqueCount(intensityKeys) > 1,
		rest: uniqueCount(restKeys) > 1,
	}
}

function hasAnyVariance(v: Variance): boolean {
	return v.reps || v.intensity || v.rest
}

function uniqueCount(values: string[]): number {
	return new Set(values).size
}

function stringifyMagnitude(m: unknown): string {
	if (m === undefined || m === null) return ''
	if (typeof m === 'number' || typeof m === 'string') return String(m)
	if (typeof m === 'object') {
		// Stable shape for progressions / nested objects so equal shapes hash equal.
		try {
			return JSON.stringify(m)
		} catch {
			return '[obj]'
		}
	}
	return String(m)
}

// ---------------------------------------------------------------------------
// Stat-tile value formatters (read directly from the workset, not per-set rows)
// ---------------------------------------------------------------------------

function formatSetCount(workset: Section['worksets'][number]): string {
	return formatNumberList(
		(workset.volume?.[0] as { sets?: unknown } | undefined)?.sets,
	)
}

/**
 * Human-readable reps summary. Single value → "5"; uniform array →
 * "5"; varying array → "varies" (caller decides if it shows here or in
 * the table). For distance-based reps, falls back to the distance label.
 */
function formatRepsSummary(workset: Section['worksets'][number]): string {
	const reps = (workset.volume?.[0] as { reps?: unknown } | undefined)?.reps
	if (typeof reps === 'string') return reps // AMRAP / AMAP literal
	if (!Array.isArray(reps)) return ''
	const labels = reps.map(formatRepValue).filter(Boolean)
	if (labels.length === 0) return ''
	const unique = Array.from(new Set(labels))
	if (unique.length === 1) return unique[0] ?? ''
	// Compact display of the variation, e.g. "4·3·2·2" — caller still flags varies.
	return unique.join(' · ')
}

function formatIntensitySummary(workset: Section['worksets'][number]): string {
	const i = workset.intensity?.[0] as
		| { magnitude?: unknown; unit?: unknown }
		| undefined
	if (!i) return ''
	const magnitudes = Array.isArray(i.magnitude) ? i.magnitude : []
	const units = Array.isArray(i.unit) ? i.unit : []
	if (magnitudes.length === 0) return ''
	if (
		!isIntensitySpecified(
			magnitudes[0],
			typeof units[0] === 'string' ? units[0] : undefined,
		)
	) {
		return ''
	}
	const magUnique = Array.from(new Set(magnitudes.map(stringifyMagnitude)))
	const unitLabel = typeof units[0] === 'string' ? units[0] : ''
	if (magUnique.length === 1) {
		return `${magUnique[0]}${unitLabel}`
	}
	// Range form for clean display, e.g., "70–100%3RM"
	const numeric = magUnique
		.map((m) => Number(m))
		.filter((n) => Number.isFinite(n))
	if (numeric.length === magUnique.length && numeric.length > 1) {
		const min = Math.min(...numeric)
		const max = Math.max(...numeric)
		return `${min}–${max}${unitLabel}`
	}
	return `${magUnique.join('·')}${unitLabel}`
}

function formatRestSummary(workset: Section['worksets'][number]): string {
	const interval = workset.interval?.[0] as { duration?: unknown[] } | undefined
	if (!interval) return ''
	const durations = Array.isArray(interval.duration) ? interval.duration : []
	const labels = durations.map(formatDuration).filter(Boolean)
	if (labels.length === 0) return ''
	const unique = Array.from(new Set(labels))
	if (unique.length === 1) return unique[0] ?? ''
	// Range form for progressing rests, e.g., "2:30–4:00"
	return `${unique[0]}–${unique[unique.length - 1]}`
}

/**
 * The parser fills missing intensity with magnitude=0, unit="lb". Treat
 * that as "no intensity given" so the tile shows an em-dash rather than
 * a misleading "0lb".
 */
function isIntensitySpecified(
	magnitude: unknown,
	unit: string | undefined,
): boolean {
	if (magnitude === undefined || magnitude === null) return false
	if (magnitude === 0 && (unit === 'lb' || unit === undefined)) return false
	return true
}

function flatten(value: unknown): unknown[] {
	if (Array.isArray(value)) return value.flatMap(flatten)
	return [value]
}

function formatNumberList(value: unknown): string {
	const flat = flatten(value).filter(
		(v) => typeof v === 'number' || typeof v === 'string',
	)
	if (flat.length === 0) return ''
	if (flat.length === 1) return String(flat[0])
	return `[${flat.join(',')}]`
}

function formatRepValue(rep: unknown): string {
	if (rep && typeof rep === 'object') {
		const r = rep as {
			repCount?: unknown
			distance?: unknown
			repMod?: unknown
		}
		// Distance-based reps (e.g., 500m row) — show the distance, not repCount=1.
		if (typeof r.distance === 'string' && r.distance.length > 0) {
			return r.distance
		}
		if (r.repCount !== undefined) return String(r.repCount)
	}
	if (Array.isArray(rep)) {
		return `[${rep.map(formatRepValue).join(',')}]`
	}
	if (rep === undefined || rep === null) return ''
	return String(rep)
}

function formatDuration(d: unknown): string {
	if (!d || typeof d !== 'object') return ''
	const obj = d as {
		minutes?: number
		seconds?: number
		totalSeconds?: number
	}
	const total = obj.totalSeconds ?? 0
	if (total === 0) return '' // treat "no rest" as empty so tiles can dim out
	const m = obj.minutes ?? Math.floor(total / 60)
	const s = obj.seconds ?? total % 60
	if (m === 0 && s === 0) {
		return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
	}
	return `${m}:${String(s).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Demo container — handles textarea auto-sizing and debounced re-parsing.
// ---------------------------------------------------------------------------

const TEXTAREA_LINE_HEIGHT_PX = 24 // matches `text-sm leading-relaxed`
const TEXTAREA_PAD_PX = 4 // small breathing room beyond scrollHeight

export function WorkoutNotationDemo({
	initial,
	caption,
	heading,
	description,
	rows = 3,
	examples,
	hideExampleMenu = false,
}: WorkoutNotationDemoProps) {
	const [source, setSource] = useState(examples?.[initial]?.notation ?? '')
	const [results, setResults] = useState<SectionParseResult[]>(
		examples?.[initial]?.parsed ?? [],
	)
	const [pulseKey, setPulseKey] = useState(0)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement | null>(null)

	const minHeightPx = rows * TEXTAREA_LINE_HEIGHT_PX

	const autosize = useCallback(() => {
		const ta = textareaRef.current
		if (!ta) return
		// Reset then measure so the textarea can shrink as well as grow.
		ta.style.height = 'auto'
		const next = Math.max(ta.scrollHeight + TEXTAREA_PAD_PX, minHeightPx)
		ta.style.height = `${next}px`
	}, [minHeightPx])

	useEffect(() => {
		autosize()
	}, [autosize, source])

	function handleExampleChange(key: string) {
		const newSource = examples?.[key]?.notation
		if (newSource) {
			handleSourceChange(newSource)
		}
	}
	function handleSourceChangeEvent(e: React.ChangeEvent<HTMLTextAreaElement>) {
		handleSourceChange(e.target.value)
	}

	function handleSourceChange(newSource: string) {
		setSource(newSource)
		// Defer measurement to next frame so the DOM has the new value.
		requestAnimationFrame(autosize)
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			setResults(parseWorkoutNotation(newSource))
			setPulseKey((k) => k + 1)
		}, 150)
	}

	return (
		<div className="overflow-hidden rounded-xl border border-m-rule bg-ink-soft">
			{examples && !hideExampleMenu ? (
				<div className="ml-4 mt-1">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								size="sm"
								className="-ml-3 h-8 data-[state=open]:bg-accent"
							>
								<span>Examples</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start">
							{Object.entries(examples).map(([key]) => {
								return (
									<DropdownMenuItem
										key={key}
										onClick={() => handleExampleChange(key)}
									>
										{key}
									</DropdownMenuItem>
								)
							})}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			) : null}
			{(caption || heading || description) && (
				<div className="border-b border-m-rule px-5 py-4">
					{caption ? <p className="text-eyebrow text-ember">{caption}</p> : null}
					{heading ? (
						<h3 className="mt-1 font-display text-display-3">{heading}</h3>
					) : null}
					{description ? (
						<p className="mt-2 text-sm leading-relaxed text-ink-fg-soft">
							{description}
						</p>
					) : null}
				</div>
			)}
			<div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
				<div className="border-b border-m-rule p-5 md:border-b-0 md:border-r">
					<p className="text-mono-label text-ink-fg-soft">
						<PulseDot className="mr-2" />
						NOTATION
					</p>
					<textarea
						ref={textareaRef}
						aria-label="Workout notation"
						value={source}
						onChange={handleSourceChangeEvent}
						rows={rows}
						style={{ minHeight: `${minHeightPx}px` }}
						className="mt-3 w-full resize-y overflow-hidden border-0 bg-transparent p-0 font-mono-display text-sm leading-relaxed text-ink-fg outline-none focus:ring-0"
						spellCheck={false}
					/>
				</div>
				<div className="p-5">
					<p className="text-mono-label text-ink-fg-soft">
						<PulseDot pulseKey={pulseKey} className="mr-2" />
						PARSED
					</p>
					<div className="mt-3">
						<ParsedOutput results={results} />
					</div>
				</div>
			</div>
		</div>
	)
}
